import React, { useEffect, useState } from 'react';
import { MapPinIcon, MinusIcon, PlusIcon, LayersIcon, InfoIcon } from 'lucide-react';
export function StickyTourMap({
  activeDay = 1,
  onDayClick
}: {
  activeDay?: number;
  onDayClick?: (day: number) => void;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [mapZoom, setMapZoom] = useState(10);
  // Simulate map loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  const handleDayClick = (day: number) => {
    if (onDayClick) {
      onDayClick(day);
    }
  };
  const handleZoomIn = () => {
    setMapZoom(prev => Math.min(prev + 1, 18));
  };
  const handleZoomOut = () => {
    setMapZoom(prev => Math.max(prev - 1, 5));
  };
  const locations = [{
    day: 1,
    name: 'Zurich',
    position: {
      top: '30%',
      left: '40%'
    }
  }, {
    day: 2,
    name: 'Lucerne',
    position: {
      top: '40%',
      left: '30%'
    }
  }, {
    day: 3,
    name: 'Interlaken',
    position: {
      top: '50%',
      left: '45%'
    }
  }, {
    day: 4,
    name: 'Grindelwald',
    position: {
      top: '60%',
      left: '55%'
    }
  }, {
    day: 5,
    name: 'Zermatt',
    position: {
      top: '70%',
      left: '35%'
    }
  }, {
    day: 6,
    name: 'Montreux',
    position: {
      top: '80%',
      left: '25%'
    }
  }, {
    day: 7,
    name: 'Geneva',
    position: {
      top: '85%',
      left: '15%'
    }
  }];
  return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden sticky top-24" data-id="element-1402">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1403">
        <h3 className="font-medium text-gray-900" data-id="element-1404">Tour Route Map</h3>
        <div className="flex items-center space-x-2" data-id="element-1405">
          <button className="p-1.5 bg-gray-100 rounded-md hover:bg-gray-200" title="Map layers" data-id="element-1406">
            <LayersIcon size={18} className="text-gray-700" data-id="element-1407" />
          </button>
          <button className="p-1.5 bg-gray-100 rounded-md hover:bg-gray-200" title="Map information" data-id="element-1408">
            <InfoIcon size={18} className="text-gray-700" data-id="element-1409" />
          </button>
        </div>
      </div>
      <div className="relative aspect-[4/3] w-full bg-blue-50" data-id="element-1410">
        {isLoading ? <div className="absolute inset-0 flex items-center justify-center" data-id="element-1411">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1412"></div>
          </div> : <>
            {/* Map image (would be replaced by actual map in production) */}
            <img src="https://images.lge.com.sg/5ce11e91-df87-44d1-a6fd-91dc91a63975.png" alt="Switzerland Tour Map" className="w-full h-full object-cover" data-id="element-1413" />
            {/* Route line - simplified for demo */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg" data-id="element-1414">
              <path d="M160,120 C200,160 120,200 180,250 C240,300 220,350 140,380" fill="none" stroke="#2563eb" strokeWidth="3" strokeLinecap="round" strokeDasharray="1, 0" data-id="element-1415" />
            </svg>
            {/* Location markers */}
            {locations.map(location => <div key={location.day} className="absolute" style={{
          top: location.position.top,
          left: location.position.left
        }} data-id="element-1416">
                <button onClick={() => handleDayClick(location.day)} className={`flex flex-col items-center group ${activeDay === location.day ? 'scale-110' : ''}`} data-id="element-1417">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${activeDay === location.day ? 'bg-blue-600 ring-4 ring-blue-200' : 'bg-gray-500 group-hover:bg-blue-500'}`} data-id="element-1418">
                    {location.day}
                  </div>
                  <div className={`mt-1 px-1.5 py-0.5 text-xs font-medium rounded whitespace-nowrap ${activeDay === location.day ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 group-hover:bg-gray-100'}`} data-id="element-1419">
                    {location.name}
                  </div>
                </button>
              </div>)}
            {/* Zoom controls */}
            <div className="absolute top-4 right-4 flex flex-col shadow-md rounded-md overflow-hidden" data-id="element-1420">
              <button onClick={handleZoomIn} className="p-2 bg-white hover:bg-gray-100 border-b border-gray-200" title="Zoom in" data-id="element-1421">
                <PlusIcon size={18} className="text-gray-700" data-id="element-1422" />
              </button>
              <button onClick={handleZoomOut} className="p-2 bg-white hover:bg-gray-100" title="Zoom out" data-id="element-1423">
                <MinusIcon size={18} className="text-gray-700" data-id="element-1424" />
              </button>
            </div>
          </>}
      </div>
      <div className="p-4 border-t border-gray-200" data-id="element-1425">
        <div className="flex items-center justify-between mb-2" data-id="element-1426">
          <h4 className="text-sm font-medium text-gray-900" data-id="element-1427">
            Journey Overview
          </h4>
          <span className="text-xs text-gray-600" data-id="element-1428">7 days, 350 km</span>
        </div>
        <div className="space-y-2 text-sm" data-id="element-1429">
          <div className="flex items-start" data-id="element-1430">
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs mr-2 flex-shrink-0 mt-0.5" data-id="element-1431">
              1
            </div>
            <div className="text-gray-700" data-id="element-1432">
              <span className="font-medium" data-id="element-1433">Days 1-2:</span> Zurich & Lucerne
            </div>
          </div>
          <div className="flex items-start" data-id="element-1434">
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs mr-2 flex-shrink-0 mt-0.5" data-id="element-1435">
              3
            </div>
            <div className="text-gray-700" data-id="element-1436">
              <span className="font-medium" data-id="element-1437">Days 3-4:</span> Interlaken &
              Grindelwald
            </div>
          </div>
          <div className="flex items-start" data-id="element-1438">
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs mr-2 flex-shrink-0 mt-0.5" data-id="element-1439">
              5
            </div>
            <div className="text-gray-700" data-id="element-1440">
              <span className="font-medium" data-id="element-1441">Day 5:</span> Zermatt & Matterhorn
            </div>
          </div>
          <div className="flex items-start" data-id="element-1442">
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs mr-2 flex-shrink-0 mt-0.5" data-id="element-1443">
              6
            </div>
            <div className="text-gray-700" data-id="element-1444">
              <span className="font-medium" data-id="element-1445">Days 6-7:</span> Montreux & Geneva
            </div>
          </div>
        </div>
        <div className="mt-4 text-xs text-gray-500" data-id="element-1446">
          <div className="flex items-center mb-1" data-id="element-1447">
            <div className="w-3 h-0.5 bg-blue-600 mr-1.5" data-id="element-1448"></div>
            <span data-id="element-1449">Train routes</span>
          </div>
          <div className="flex items-center" data-id="element-1450">
            <div className="w-3 h-0.5 bg-green-600 mr-1.5 border-dashed" data-id="element-1451"></div>
            <span data-id="element-1452">Scenic routes</span>
          </div>
        </div>
      </div>
    </div>;
}